import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './LoginModal.css';

const LoginModal = ({ isOpen, onClose, onSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null);
  const [error, setError] = useState('');

  const { login, signup, loginWithGoogle, currentUser } = useAuth();
  const navigate = useNavigate();

  // Instant-Stop logic for Excellent UX
  React.useEffect(() => {
    if (currentUser && isOpen) {
      setLoading(false);
      setSocialLoading(null);
      // Trigger SUCCESS immediately for premium feel
      onSuccess && onSuccess(currentUser);
      onClose();
    }
  }, [currentUser, isOpen, onSuccess, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      let result;
      if (isLogin) {
        result = await login(email, password);
      } else {
        result = await signup(email, password);
      }

      if (result.success) {
        onSuccess && onSuccess(result.user);
        onClose();
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('An unexpected error occurred');
    }

    setLoading(false);
  };


  const handleGoogleLogin = async () => {
    setError('');
    setSocialLoading('google');
    // Don't set main 'loading' state for social logins to avoid double spinners

    // Safety net: 60s timeout for true network hangs.
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('timeout')), 60000)
    );

    // Watchdog to rescue UI if Firebase SDK hangs resolving a closed popup.
    // We wait 8 seconds after the window regains focus to allow Firebase 
    // to finish profile creation for new users.
    let focusTimer;
    const handleFocus = () => {
      focusTimer = setTimeout(() => {
        setSocialLoading(prev => {
          if (prev) {
            setError('Sign in process was canceled or interrupted.');
            return null;
          }
          return prev;
        });
      }, 1000); // 1s is responsive for cancellation while allowing successful resolve
      window.removeEventListener('focus', handleFocus);
    };
    window.addEventListener('focus', handleFocus);

    try {
      const result = await Promise.race([loginWithGoogle(), timeoutPromise]);
      if (result.success) {
        onSuccess && onSuccess(result.user);
        onClose();
        setSocialLoading(null);
        return;
      } else {
        // result.error is null when user intentionally closed the popup — stay silent
        if (result.error) {
          setError(result.error);
        }
      }
    } catch (error) {
      if (error.message === 'timeout') {
        setError('Sign in is taking too long. Please check your connection and try again.');
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      window.removeEventListener('focus', handleFocus);
      clearTimeout(focusTimer);
      setSocialLoading(null);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="login-modal-overlay">
      <div className="login-modal-container">
        <button className="login-modal-close" onClick={onClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className="login-modal-content">
          <div className="login-modal-header">
            <h2>{isLogin ? 'Sign In' : 'Sign Up'}</h2>
            <p>{isLogin ? 'Welcome back! Sign in to continue.' : 'Create an account to get started.'}</p>
          </div>

          {error && (
            <div className="login-modal-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-modal-form">
            <div className="login-modal-field">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
              />
            </div>

            <div className="login-modal-field">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                minLength="6"
              />
            </div>

            {!isLogin && (
              <div className="login-modal-field">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm your password"
                  minLength="6"
                />
              </div>
            )}

            <button 
              type="submit" 
              className="login-modal-submit"
              disabled={loading}
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Sign Up')}
            </button>
          </form>

          <div className="login-modal-divider">
            <span>or</span>
          </div>

          <button 
            className="login-modal-google"
            onClick={handleGoogleLogin}
            disabled={loading}
          >
            {socialLoading === 'google' ? (
              <span className="login-modal-spinner"></span>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            {socialLoading === 'google' ? 'Connecting...' : 'Continue with Google'}
          </button>

          <div className="login-modal-switch">
            <p>
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button 
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="login-modal-switch-button"
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
